import * as Axios from "axios";
import { AxiosInstance, AxiosResponse } from "axios";

import { FormSchema } from "@graffer-inc/form-schema";
import {
  SurveyAnswerType,
  SurveyPublishSetting,
  SurveyPublishStatus,
  SurveyType,
} from "@graffer-inc/cloud-gov-core";

import {
  convert,
  HttpMethod,
  Resources,
  ResourcesV2,
  TypedCloudGovResource,
} from "./base";
import { SurveyEntity } from "../domain/surveys/survey-entity";
import {
  SurveyImportStatusType,
  SurveyImportStatus,
} from "../domain/surveys/survey-import-status";
import { SurveyAnswer } from "../domain/surveys/survey-answer";
import { Group } from "../domain/groups/group";
import { CSVEncoding } from "fe-dashboard/src/application/domain/file-encoding";
import type { SurveyCsvMetaID } from "../domain/surveys/csv-meta-field";

export class CloudGovSurveyClient {
  http: AxiosInstance;

  constructor(axios: AxiosInstance) {
    this.http = axios;
  }

  // surveys
  fetchSurvey = (id: string) =>
    TypedCloudGovResource<SurveyType, never>(this)(
      `/surveys/${id}`,
      HttpMethod.GET,
    ).then(convert(SurveyEntity.fromJSON));

  fetchSurveyStats = (id: string) =>
    TypedCloudGovResource<
      {
        numOfTotalAnswer: number;
        numOfRecent24HoursAnswers: number;
      },
      never
    >(this)(`/surveys/${id}/stats`, HttpMethod.GET);

  downloadSurveyAnswersCSV = (
    surveyId: string,
    dates: { dateFrom?: string; dateTo?: string },
    surveyMetaIds: SurveyCsvMetaID[],
    requireSelectableRawValue: boolean,
    encoding: CSVEncoding,
  ) =>
    ResourcesV2(this)(`/surveys/${surveyId}/download-csv`, HttpMethod.GET, {
      responseType: "blob",
      dataType: "binary",
      params: {
        ...dates,
        surveyMetaIds,
        requireSelectableRawValue,
        encoding,
      },
    });

  fetchSurveysByTenantId = (
    tenantId: string,
    page: number,
    limit: number,
    filter?: {
      query?: string;
    },
  ) =>
    TypedCloudGovResource<SurveyType[], any>(this)(
      `/surveys/${tenantId}/organizations?page=${page}&limit=${limit}`,
      HttpMethod.GET,
      {
        params: {
          ...filter,
        },
      },
    ).then(convert(SurveyEntity.fromJSONArray));

  updateSurvey = (
    id: string,
    name: string,
    formSchema: FormSchema,
    isExcludeFromSearch: boolean,
    isNotifyArrival: boolean,
    customPath?: string,
  ) =>
    TypedCloudGovResource<
      SurveyType,
      {
        name: string;
        formSchema: FormSchema;
        isExcludeFromSearch: boolean;
        isNotifyArrival: boolean;
        customPath?: string;
      }
    >(this)(`/surveys/${id}`, HttpMethod.PUT, {
      name: name,
      formSchema: formSchema,
      isExcludeFromSearch,
      isNotifyArrival,
      customPath,
    }).then(convert(SurveyEntity.fromJSON));

  deleteSurvey = (id: string) =>
    Resources(this)(`/surveys/${id}`, HttpMethod.DELETE);

  returnToDraftSurvey = (id: string) =>
    TypedCloudGovResource<SurveyType, {}>(this)(
      `/surveys/${id}/return-to-draft`,
      HttpMethod.PUT,
      {},
    ).then(convert(SurveyEntity.fromJSON));

  updateSurveyPublishSetting = (
    id: string,
    isPublishNow: boolean,
    isStartAcceptAtPublish: boolean,
    setting: SurveyPublishSetting,
  ) =>
    TypedCloudGovResource<SurveyType, {}>(this)(
      `/surveys/${id}/publish-setting`,
      HttpMethod.PUT,
      {
        isPublishNow: isPublishNow,
        isStartAcceptAtPublish: isStartAcceptAtPublish,
        publishSetting: setting,
      },
    ).then(convert(SurveyEntity.fromJSON));

  publishSurvey = (id: string) =>
    TypedCloudGovResource<SurveyType, {}>(this)(
      `/surveys/${id}/publish`,
      HttpMethod.PUT,
      {},
    ).then(convert(SurveyEntity.fromJSON));

  unpublishSurvey = (id: string) =>
    TypedCloudGovResource<SurveyType, {}>(this)(
      `/surveys/${id}/unpublish`,
      HttpMethod.PUT,
      {},
    ).then(convert(SurveyEntity.fromJSON));

  pauseAcceptingSurvey = (id: string) =>
    TypedCloudGovResource<SurveyType, {}>(this)(
      `/surveys/${id}/pause-accepting`,
      HttpMethod.PUT,
      {},
    ).then(convert(SurveyEntity.fromJSON));

  resumeAcceptingSurvey = (id: string) =>
    TypedCloudGovResource<SurveyType, {}>(this)(
      `/surveys/${id}/resume-accepting`,
      HttpMethod.PUT,
      {},
    ).then(convert(SurveyEntity.fromJSON));

  fetchSurveyAnswers = (id: string, page: number, limit: number) =>
    TypedCloudGovResource<SurveyAnswerType[], never>(this)(
      `/survey-answers?surveyId=${id}&page=${page}&limit=${limit}`,
      HttpMethod.GET,
    ).then(convert(SurveyAnswer.fromJSONArray));

  copySurvey = (id: string, name: string) =>
    TypedCloudGovResource<SurveyType, { name: string }>(this)(
      `/surveys/${id}/copy`,
      HttpMethod.POST,
      {
        name: name,
      },
    ).then(convert(SurveyEntity.fromJSON));

  exportSurveySettings = (surveyId: string) =>
    Resources(this)(`/surveys/${surveyId}/export`, HttpMethod.GET);

  exportEncryptedSurveySettings = (surveyId: string) =>
    Resources(this)(`/surveys/${surveyId}/export-encrypted`, HttpMethod.GET, {
      responseType: "blob",
      dataType: "binary",
    });

  importSurveyFromFile = (
    jsonData: File,
    tenantId: string,
    groupId: string,
  ) => {
    const form = new FormData();
    form.append("tenantId", tenantId);
    form.append("groupId", groupId);
    form.append("surveyFile", jsonData);
    return Axios.default
      .post<FormData, AxiosResponse<SurveyType>>(
        `/files/v2/surveys-import`,
        form,
        setNoCache({
          headers: {
            "content-type": "multipart/form-data",
          },
        }),
      )
      .then(convert(SurveyEntity.fromJSON));
  };

  checkSurveyImportSerializedFile = (zipData: File) => {
    const form = new FormData();
    form.append("surveyFile", zipData);
    return Axios.default
      .post<FormData, AxiosResponse<SurveyImportStatusType>>(
        `/api/file-upload/surveys-import/check`,
        form,
        setNoCache({
          headers: {
            "content-type": "multipart/form-data",
          },
        }),
      )
      .then(convert(SurveyImportStatus.fromJSON));
  };

  importSurveyFromZipFile = (
    zipData: File,
    groupId: string,
    shallClearCustomPath: boolean,
  ) => {
    const form = new FormData();
    if (!!groupId) {
      form.append("groupId", groupId);
    }
    form.append("surveyFile", zipData);
    form.append(
      "shallClearCustomPath",
      shallClearCustomPath ? "true" : "false",
    );
    return Axios.default
      .post<FormData, AxiosResponse<SurveyType>>(
        `/api/file-upload/surveys-import`,
        form,
        setNoCache({
          headers: {
            "content-type": "multipart/form-data",
          },
        }),
      )
      .then(convert(SurveyEntity.fromJSON));
  };

  fetchSurveys = (
    page: number,
    limit: number,
    filter?: {
      query?: string;
      publishStatus?: SurveyPublishStatus[];
    },
  ) =>
    TypedCloudGovResource<SurveyType[], any>(this)(
      `/surveys?page=${page}&limit=${limit}`,
      HttpMethod.GET,
      {
        params: {
          ...filter,
        },
      },
    ).then(convert(SurveyEntity.fromJSONArray));

  createSurvey = (name: string, groupId: string, formSchema: FormSchema) =>
    TypedCloudGovResource<
      SurveyType,
      { name: string; groupId: string; formSchema: FormSchema }
    >(this)(`/surveys`, HttpMethod.POST, {
      name: name,
      groupId: groupId,
      formSchema: formSchema,
    });

  updateGroupToSurvey = (id: string, groupId: string) =>
    TypedCloudGovResource<SurveyType, any>(this)(
      `/surveys/${id}/groups`,
      HttpMethod.PUT,
      {
        groupId,
      },
    ).then(convert(SurveyEntity.fromJSON));

  // cloud-gov
  fetchGroupById = (groupId: string) =>
    TypedCloudGovResource<Group, {}>(this)(
      `/groups/${groupId}`,
      HttpMethod.GET,
    );

  fetchSurveyByCustomPath = (tenantId: string, customPath: string) =>
    TypedCloudGovResource<SurveyType, any>(this)(
      `/organizations/${tenantId}/survey-custom-path/${customPath}`,
      HttpMethod.GET,
    ).then(convert(SurveyEntity.fromJSON));

  generateSurveyPreviewToken = (surveyId: string) =>
    TypedCloudGovResource<{ previewToken: string }, {}>(this)(
      `/surveys/${surveyId}/issue-preview-token`,
      HttpMethod.POST,
      {},
    );
}

export const setNoCache = (config: Axios.AxiosRequestConfig) => {
  if (!config.params) {
    config.params = {};
  }
  if (
    typeof URLSearchParams === "function" &&
    config.params instanceof URLSearchParams
  ) {
    config.params.append("_", Date.now().toString());
  } else {
    config.params._ = Date.now();
  }
  return config;
};

const client = Axios.default.create({
  headers: {
    Accept: "application/json",
    "Content-Type": "application/json",
  },
});

client.interceptors.request.use(setNoCache);

export const surveyApiClient = new CloudGovSurveyClient(client);
